import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Badge, Input, Button, Nav, NavItem, NavLink,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Spinner, Alert,
} from "reactstrap";
import classnames from "classnames";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── types API ── */
interface DictItem {
    id: number;
    code: string;
    label: string;
    color?: string;
    description?: string;
    pos?: number;
    use_default?: number;
}

interface TicketsDictionaries {
    types: DictItem[];
    severities: DictItem[];
    categories: DictItem[];
    resolutions: DictItem[];
}

interface ApiTicket {
    id: number;
    ref: string;
    track_id: string;
    subject: string;
    message: string;
    status: number;         // 0,1,2,3,5,7,8,9
    status_label: string;
    progress: number;
    type_code?: string;
    type_label?: string;
    category_code?: string;
    category_label?: string;
    severity_code?: string;
    severity_label?: string;
    severity_color?: string;
    date_created: string;
    date_read?: string | null;
    date_close?: string | null;
    fk_user_create: number;
    fk_user_assign: number;
}

interface TicketsCounts {
    total: number;
    open: number;
    pending: number;
    resolved: number;
}

/* ── status Dolibarr → couleur / icône ── */
const statusColor = (st: number): string => {
    if (st === 0 || st === 1) return "primary";   // Nouveau, Lu
    if (st === 2 || st === 3) return "info";      // Assigné, En cours
    if (st === 5 || st === 7) return "warning";   // Besoin infos, En attente
    if (st === 8)              return "success";  // Résolu
    if (st === 9)              return "secondary";// Annulé
    return "secondary";
};
const statusIcon = (st: number): string => {
    if (st === 0)               return "ri-mail-line";
    if (st === 1)               return "ri-mail-open-line";
    if (st === 2 || st === 3)   return "ri-loader-4-line";
    if (st === 5 || st === 7)   return "ri-time-line";
    if (st === 8)               return "ri-checkbox-circle-line";
    if (st === 9)               return "ri-close-circle-line";
    return "ri-ticket-line";
};
const isOpenStatus    = (st: number) => st === 0 || st === 1 || st === 2 || st === 3;
const isPendingStatus = (st: number) => st === 5 || st === 7;
const isClosedStatus  = (st: number) => st === 8 || st === 9;

const categoryIconFromCode = (code?: string): string => {
    switch ((code || "").toUpperCase()) {
        case "TECHNICAL":
        case "TECH":      return "ri-tools-line";
        case "BILLING":
        case "FACTURATION": return "ri-bill-line";
        case "COMMERCIAL":
        case "COM":       return "ri-shopping-bag-line";
        default:          return "ri-question-line";
    }
};


/* ── helpers ── */
const formatDate = (raw: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

const formatRelative = (raw: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    const diff = Date.now() - d.getTime();
    const h = Math.floor(diff / (1000 * 60 * 60));
    if (h < 1) return "il y a quelques minutes";
    if (h < 24) return `il y a ${h}h`;
    const j = Math.floor(h / 24);
    if (j < 7) return `il y a ${j}j`;
    return d.toLocaleDateString("fr-FR");
};

/* ── composant ── */
const Support = () => {
    const [tickets, setTickets] = useState<ApiTicket[]>([]);
    const [counts, setCounts]   = useState<TicketsCounts>({ total: 0, open: 0, pending: 0, resolved: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState<string | null>(null);

    const [dicts, setDicts] = useState<TicketsDictionaries>({ types: [], severities: [], categories: [], resolutions: [] });

    const [statusFilter, setStatusFilter] = useState<"all" | "open" | "pending" | "resolved">("all");
    const [search, setSearch] = useState("");

    const [modalNew, setModalNew] = useState(false);
    const [newForm, setNewForm] = useState({
        subject: "",
        type_code: "",
        severity_code: "",
        category_code: "OTHER",
        message: "",
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);
    const [createSuccess, setCreateSuccess] = useState<string | null>(null);

    const [modalDetail, setModalDetail] = useState(false);
    const [selected, setSelected] = useState<ApiTicket | null>(null);
    const [reply, setReply] = useState("");

    /* ── chargement initial ── */
    const fetchTickets = async () => {
        try {
            const res: any = await axios.post(
                "/apiV2/",
                { action: "tickets", subaction: "list" },
                apiV2PostConfig()
            );
            const payload = res?.data ?? res ?? { items: [], counts: { total: 0, open: 0, pending: 0, resolved: 0 } };
            setTickets(payload.items || []);
            setCounts(payload.counts || { total: 0, open: 0, pending: 0, resolved: 0 });
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message || "Erreur lors de la récupération des tickets.");
        }
    };

    const fetchDictionaries = async () => {
        try {
            const res: any = await axios.post(
                "/apiV2/",
                { action: "tickets", subaction: "dictionaries" },
                apiV2PostConfig()
            );
            const payload = res?.data ?? res;
            if (payload) {
                setDicts({
                    types:       payload.types       || [],
                    severities:  payload.severities  || [],
                    categories:  payload.categories  || [],
                    resolutions: payload.resolutions || [],
                });
            }
        } catch {
            // silencieux : le formulaire fonctionnera quand même avec des selects vides
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchTickets(), fetchDictionaries()]);
            setLoading(false);
        })();
    }, []);

    /* ── filtre ── */
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return tickets.filter(t => {
            if (statusFilter === "open"     && !isOpenStatus(t.status))    return false;
            if (statusFilter === "pending"  && !isPendingStatus(t.status)) return false;
            if (statusFilter === "resolved" && !isClosedStatus(t.status))  return false;
            if (!q) return true;
            return (
                (t.ref || "").toLowerCase().includes(q) ||
                (t.subject || "").toLowerCase().includes(q) ||
                (t.message || "").toLowerCase().includes(q)
            );
        });
    }, [tickets, statusFilter, search]);

    const openNewModal = () => {
        // defaut : premier type use_default=1 trouve, sinon premier
        const defaultType = dicts.types.find(t => t.use_default === 1)?.code
                         || dicts.types[0]?.code || "";
        const defaultSev  = dicts.severities.find(s => s.use_default === 1)?.code
                         || dicts.severities[0]?.code || "";
        setNewForm({
            subject: "",
            type_code: defaultType,
            severity_code: defaultSev,
            category_code: "OTHER",
            message: "",
        });
        setCreateError(null);
        setModalNew(true);
    };

    const submitNewTicket = async () => {
        if (!newForm.subject.trim() || !newForm.message.trim()) {
            setCreateError("Le sujet et la description sont requis.");
            return;
        }
        setCreating(true);
        setCreateError(null);
        try {
            const res: any = await axios.post(
                "/apiV2/",
                {
                    action: "tickets",
                    subaction: "create",
                    subject: newForm.subject,
                    message: newForm.message,
                    type_code: newForm.type_code || undefined,
                    severity_code: newForm.severity_code || undefined,
                    category_code: newForm.category_code || "OTHER",
                },
                apiV2PostConfig()
            );
            const payload = res?.data ?? res;
            const ref = payload?.data?.ref || payload?.ref || "";
            setModalNew(false);
            setCreateSuccess(`Ticket ${ref ? ref + " " : ""}créé avec succès.`);
            await fetchTickets();
        } catch (e: any) {
            setCreateError(typeof e === "string" ? e : e?.message || "Erreur lors de la création du ticket.");
        } finally {
            setCreating(false);
        }
    };

    const openDetail = (t: ApiTicket) => {
        setSelected(t);
        setReply("");
        setModalDetail(true);
    };

    const sendReply = () => {
        // TODO : brancher sur une future subaction `reply` de l'API tickets
        setReply("");
    };

    const widgets = [
        { label: "Tickets totaux",  counter: counts.total,    icon: "ri-ticket-2-line",        color: "primary" },
        { label: "Ouverts",         counter: counts.open,     icon: "ri-mail-open-line",       color: "info"    },
        { label: "En attente",      counter: counts.pending,  icon: "ri-time-line",            color: "warning" },
        { label: "Résolus / fermés", counter: counts.resolved, icon: "ri-checkbox-circle-line", color: "success" },
    ];

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Support & Aide" pageTitle="Espace Client" />

                    {error && (
                        <Alert color="danger" className="mb-4" toggle={() => setError(null)}>
                            <i className="ri-error-warning-line me-2"></i>{error}
                        </Alert>
                    )}
                    {createSuccess && (
                        <Alert color="success" className="mb-4" toggle={() => setCreateSuccess(null)}>
                            <i className="ri-checkbox-circle-line me-2"></i>{createSuccess}
                        </Alert>
                    )}

                    {/* ── KPI ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Aide rapide ── */}
                    <Row>
                        <Col lg={12}>
                            <Card className="mb-4" style={{ background: "linear-gradient(135deg, #0066A1 0%, #00A389 100%)" }}>
                                <CardBody className="text-white p-4">
                                    <Row className="align-items-center">
                                        <Col md={8}>
                                            <h4 className="text-white mb-2">
                                                <i className="ri-customer-service-2-line me-2"></i>
                                                Besoin d'aide ?
                                            </h4>
                                            <p className="text-white-75 mb-0">
                                                Notre équipe support est disponible du lundi au vendredi de 9h à 18h.
                                                Créez un ticket et nous vous répondrons dans les plus brefs délais.
                                            </p>
                                            <div className="mt-3 d-flex flex-wrap gap-3">
                                                <div>
                                                    <i className="ri-phone-line me-1"></i>
                                                    <a href="tel:+33971167373" className="text-white text-decoration-none">
                                                        <strong>09 71 16 73 73</strong>
                                                    </a>
                                                </div>
                                                <div>
                                                    <i className="ri-mail-line me-1"></i>
                                                    <a href="mailto:support@iptrend.fr" className="text-white text-decoration-none">
                                                        <strong>support@iptrend.fr</strong>
                                                    </a>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                                            <Button color="light" size="lg" onClick={openNewModal}>
                                                <i className="ri-add-circle-line me-2"></i>
                                                Nouveau ticket
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── Liste tickets ── */}
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="border-0">
                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                        <h5 className="card-title mb-0 flex-grow-1">Mes tickets</h5>
                                        <div className="search-box">
                                            <Input
                                                type="text"
                                                className="form-control search"
                                                placeholder="Rechercher un ticket…"
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                style={{ minWidth: 240 }}
                                            />
                                            <i className="ri-search-line search-icon"></i>
                                        </div>
                                        <Button color="primary" size="sm" onClick={openNewModal}>
                                            <i className="ri-add-line me-1"></i>Nouveau
                                        </Button>
                                    </div>

                                    <Nav pills className="nav-pills-custom gap-2 mt-3">
                                        {[
                                            { k: "all",      label: "Tous",      count: counts.total    },
                                            { k: "open",     label: "Ouverts",   count: counts.open     },
                                            { k: "pending",  label: "En attente", count: counts.pending  },
                                            { k: "resolved", label: "Résolus",   count: counts.resolved },
                                        ].map(t => (
                                            <NavItem key={t.k}>
                                                <NavLink
                                                    className={classnames({ active: statusFilter === t.k })}
                                                    onClick={() => setStatusFilter(t.k as any)}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    {t.label}
                                                    <Badge color="light" className="ms-1 text-dark">{t.count}</Badge>
                                                </NavLink>
                                            </NavItem>
                                        ))}
                                    </Nav>
                                </CardHeader>

                                <CardBody className="pt-0">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <Spinner style={{ color: "#0066A1" }} />
                                            <p className="text-muted mt-2">Chargement des tickets…</p>
                                        </div>
                                    ) : filtered.length === 0 ? (
                                        <div className="text-center py-5">
                                            <div className="avatar-md mx-auto mb-3">
                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                    <i className="ri-inbox-line"></i>
                                                </span>
                                            </div>
                                            <h5 className="mt-2">Aucun ticket trouvé</h5>
                                            <p className="text-muted">
                                                {search ? "Aucun résultat pour cette recherche." : "Vous n'avez aucun ticket pour ce filtre."}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="vstack gap-2">
                                            {filtered.map(t => {
                                                const col = statusColor(t.status);
                                                return (
                                                <div
                                                    key={t.id}
                                                    className="border rounded p-3"
                                                    style={{ cursor: "pointer", transition: "all 0.2s" }}
                                                    onClick={() => openDetail(t)}
                                                    onMouseEnter={e => e.currentTarget.classList.add("bg-light")}
                                                    onMouseLeave={e => e.currentTarget.classList.remove("bg-light")}
                                                >
                                                    <div className="d-flex align-items-start gap-3">
                                                        <div className="avatar-sm flex-shrink-0">
                                                            <span className={`avatar-title rounded-circle bg-${col}-subtle text-${col} fs-18`}>
                                                                <i className={categoryIconFromCode(t.category_code)}></i>
                                                            </span>
                                                        </div>
                                                        <div className="flex-grow-1 min-width-0">
                                                            <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
                                                                <div>
                                                                    <h6 className="mb-1 fs-14">
                                                                        <span className="text-muted fs-12 fw-normal me-2">{t.ref}</span>
                                                                        {t.subject}
                                                                    </h6>
                                                                    <p className="text-muted fs-13 mb-0 text-truncate" style={{ maxWidth: 520 }}>
                                                                        {t.message}
                                                                    </p>
                                                                </div>
                                                                <div className="text-end">
                                                                    <Badge className={`bg-${col}-subtle text-${col} mb-1`}>
                                                                        <i className={`${statusIcon(t.status)} me-1`}></i>
                                                                        {t.status_label}
                                                                    </Badge>
                                                                    <div className="text-muted fs-11">
                                                                        {formatRelative(t.date_created)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                                                {t.type_label && (
                                                                    <Badge color="light" className="text-dark fs-11 fw-normal">
                                                                        <i className="ri-price-tag-3-line me-1"></i>
                                                                        {t.type_label}
                                                                    </Badge>
                                                                )}
                                                                {t.category_label && (
                                                                    <Badge color="light" className="text-dark fs-11 fw-normal">
                                                                        <i className={`${categoryIconFromCode(t.category_code)} me-1`}></i>
                                                                        {t.category_label}
                                                                    </Badge>
                                                                )}
                                                                {t.severity_label && (
                                                                    <Badge
                                                                        className="fs-11 fw-normal text-white"
                                                                        style={t.severity_color ? { backgroundColor: t.severity_color } : {}}
                                                                    >
                                                                        <i className="ri-flag-line me-1"></i>
                                                                        {t.severity_label}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* ── FAQ ── */}
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <h5 className="card-title mb-0">
                                        <i className="ri-question-line me-2 text-primary"></i>
                                        Questions fréquentes
                                    </h5>
                                </CardHeader>
                                <CardBody>
                                    <Row className="g-3">
                                        {[
                                            { icon: "ri-sim-card-line",      title: "Comment activer une nouvelle SIM ?",    desc: "Retrouvez la procédure d'activation complète pour vos nouvelles cartes SIM et eSIM." },
                                            { icon: "ri-bill-line",          title: "Comprendre ma facture",                  desc: "Guide détaillé pour lire et comprendre votre facture mensuelle IPTrend." },
                                            { icon: "ri-signal-wifi-line",   title: "Ma ligne ne fonctionne plus",            desc: "Premiers gestes à effectuer avant de contacter le support technique." },
                                            { icon: "ri-settings-3-line",    title: "Gérer mes utilisateurs",                 desc: "Ajouter, modifier ou désactiver un utilisateur depuis votre espace client." },
                                        ].map((f, i) => (
                                            <Col md={6} key={i}>
                                                <div className="border rounded p-3 h-100" style={{ cursor: "pointer" }}>
                                                    <div className="d-flex gap-3">
                                                        <div className="avatar-xs flex-shrink-0">
                                                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-16">
                                                                <i className={f.icon}></i>
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <h6 className="mb-1 fs-14">{f.title}</h6>
                                                            <p className="text-muted fs-12 mb-0">{f.desc}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                </Container>
            </div>

            {/* ── Modal : Nouveau ticket ── */}
            <Modal isOpen={modalNew} toggle={() => !creating && setModalNew(false)} size="lg" centered>
                <ModalHeader toggle={() => !creating && setModalNew(false)} className="border-bottom-dashed">
                    <i className="ri-add-circle-line me-2 text-primary"></i>
                    Nouveau ticket de support
                </ModalHeader>
                <ModalBody className="p-4">
                    {createError && (
                        <Alert color="danger" className="mb-3">
                            <i className="ri-error-warning-line me-2"></i>{createError}
                        </Alert>
                    )}
                    <Row className="g-3">
                        <Col xs={12}>
                            <label className="form-label text-muted fw-medium fs-13">Sujet <span className="text-danger">*</span></label>
                            <Input
                                type="text"
                                placeholder="Décrivez brièvement votre demande"
                                value={newForm.subject}
                                onChange={e => setNewForm(f => ({ ...f, subject: e.target.value }))}
                            />
                        </Col>
                        <Col md={6}>
                            <label className="form-label text-muted fw-medium fs-13">Type de demande</label>
                            <select
                                className="form-select"
                                value={newForm.type_code}
                                onChange={e => setNewForm(f => ({ ...f, type_code: e.target.value }))}
                            >
                                <option value="">—</option>
                                {dicts.types.map(t => (
                                    <option key={t.code} value={t.code}>{t.label}</option>
                                ))}
                            </select>
                        </Col>
                        <Col md={6}>
                            <label className="form-label text-muted fw-medium fs-13">Sévérité</label>
                            <select
                                className="form-select"
                                value={newForm.severity_code}
                                onChange={e => setNewForm(f => ({ ...f, severity_code: e.target.value }))}
                            >
                                <option value="">—</option>
                                {dicts.severities.map(s => (
                                    <option key={s.code} value={s.code}>{s.label}</option>
                                ))}
                            </select>
                        </Col>
                        <Col xs={12}>
                            <label className="form-label text-muted fw-medium fs-13">Description détaillée <span className="text-danger">*</span></label>
                            <Input
                                type="textarea"
                                rows={6}
                                placeholder="Décrivez votre problème ou votre demande en détail&#10;(nombre de lignes concernées, numéros, messages d'erreur, etc.)"
                                value={newForm.message}
                                onChange={e => setNewForm(f => ({ ...f, message: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12}>
                            <div className="border border-dashed rounded p-3 text-center text-muted">
                                <i className="ri-attachment-2 fs-2 d-block mb-1"></i>
                                <p className="mb-0 fs-13">Glissez vos fichiers ici ou cliquez pour joindre (bientôt disponible)</p>
                            </div>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter className="border-top-dashed">
                    <Button color="light" onClick={() => setModalNew(false)} disabled={creating}>
                        Annuler
                    </Button>
                    <Button color="primary" onClick={submitNewTicket} disabled={creating || !newForm.subject.trim() || !newForm.message.trim()}>
                        {creating
                            ? <><Spinner size="sm" className="me-1" />Création…</>
                            : <><i className="ri-send-plane-line me-1"></i>Envoyer le ticket</>
                        }
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ── Modal : Détail ticket ── */}
            <Modal isOpen={modalDetail} toggle={() => setModalDetail(false)} size="lg" centered scrollable>
                <ModalHeader toggle={() => setModalDetail(false)} className="border-bottom-dashed">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="text-muted fs-12">{selected?.ref}</span>
                            {selected && (
                                <Badge className={`bg-${statusColor(selected.status)}-subtle text-${statusColor(selected.status)}`}>
                                    <i className={`${statusIcon(selected.status)} me-1`}></i>
                                    {selected.status_label}
                                </Badge>
                            )}
                        </div>
                        <span className="fw-semibold fs-15">{selected?.subject}</span>
                    </div>
                </ModalHeader>
                <ModalBody className="p-0" style={{ background: "#f8f9fa" }}>
                    {selected && (
                        <>
                            {/* Meta */}
                            <div className="p-3 bg-white border-bottom">
                                <Row className="g-2">
                                    {selected.type_label && (
                                        <Col md={4}>
                                            <p className="text-muted fs-11 mb-1 text-uppercase">Type</p>
                                            <p className="mb-0 fs-13">
                                                <i className="ri-price-tag-3-line me-1"></i>
                                                {selected.type_label}
                                            </p>
                                        </Col>
                                    )}
                                    {selected.category_label && (
                                        <Col md={4}>
                                            <p className="text-muted fs-11 mb-1 text-uppercase">Catégorie</p>
                                            <p className="mb-0 fs-13">
                                                <i className={`${categoryIconFromCode(selected.category_code)} me-1`}></i>
                                                {selected.category_label}
                                            </p>
                                        </Col>
                                    )}
                                    {selected.severity_label && (
                                        <Col md={4}>
                                            <p className="text-muted fs-11 mb-1 text-uppercase">Sévérité</p>
                                            <Badge
                                                className="text-white"
                                                style={selected.severity_color ? { backgroundColor: selected.severity_color } : {}}
                                            >
                                                <i className="ri-flag-line me-1"></i>
                                                {selected.severity_label}
                                            </Badge>
                                        </Col>
                                    )}
                                    <Col md={12}>
                                        <p className="text-muted fs-11 mb-1 text-uppercase">Créé le</p>
                                        <p className="mb-0 fs-13">{formatDate(selected.date_created)}</p>
                                    </Col>
                                </Row>
                            </div>

                            {/* Message initial */}
                            <div className="p-3">
                                <div className="d-flex gap-2 flex-row-reverse">
                                    <div className="avatar-xs flex-shrink-0">
                                        <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-13 fw-semibold">
                                            <i className="ri-user-line"></i>
                                        </span>
                                    </div>
                                    <div className="bg-white rounded p-3 shadow-sm" style={{ maxWidth: "75%" }}>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span className="fw-semibold fs-13">Vous</span>
                                            <span className="text-muted fs-11">{formatRelative(selected.date_created)}</span>
                                        </div>
                                        <p className="mb-0 fs-13 text-body" style={{ whiteSpace: "pre-wrap" }}>{selected.message}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Zone réponse */}
                            {!isClosedStatus(selected.status) && (
                                <div className="p-3 bg-white border-top">
                                    <Input
                                        type="textarea"
                                        rows={3}
                                        placeholder="Écrire une réponse… (bientôt disponible)"
                                        value={reply}
                                        onChange={e => setReply(e.target.value)}
                                        disabled
                                    />
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <button type="button" className="btn btn-link btn-sm text-muted p-0" disabled>
                                            <i className="ri-attachment-2 me-1"></i>Joindre un fichier
                                        </button>
                                        <Button color="primary" size="sm" onClick={sendReply} disabled>
                                            <i className="ri-send-plane-line me-1"></i>Envoyer
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
            </Modal>

        </React.Fragment>
    );
};

export default Support;
